import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  Output,
  ViewChild,
} from '@angular/core';
import { DateRange as DTDateRange } from '@angular/material/datepicker';
import { MatMenuTrigger } from '@angular/material/menu';
import { PERIOD_MENU_ITEMS, PERIOD_NAMES } from 'src/app/core/dictionaries/period-names';
import { PeriodEnum, PeriodMenuItem } from 'src/app/core/models/period';
import {
  formatPeriod,
  getUTCDateRangeByDate,
  getUTCDateRangeByPeriod,
  toLocalDate,
} from 'src/app/core/utils/date-utils';

export type PeriodParams = {
  readonly period?: PeriodEnum;
  readonly start?: number;
  readonly end?: number;
};

@Component({
  selector: 'craft-filter-period-select',
  templateUrl: './craft-filter-period-select.component.html',
  styleUrls: ['./craft-filter-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CraftFilterPeriodSelectComponent implements OnChanges {
  @Input() public period?: PeriodEnum;
  @Input() public start?: number;
  @Input() public end?: number;
  @Input() public buttonText: string;
  @Output() public apply = new EventEmitter<PeriodParams>();
  @Output() public remove = new EventEmitter<void>();
  @ViewChild('menuTrigger') menuTrigger: MatMenuTrigger;

  public dateRange?: DTDateRange<Date>;
  public items = PERIOD_MENU_ITEMS.filter((p) => p.id !== PeriodEnum.custom);

  constructor() {}

  @HostBinding('class.active')
  public get isAnySelect() {
    return !!this.period;
  }

  public get buttonExtendedText(): string {
    if (!this.period) {
      return this.buttonText;
    }

    if (this.dateRange) {
      return `${this.buttonText}: ${formatPeriod(this.dateRange.start, this.dateRange.end)}`;
    }

    return `${this.buttonText}: ${PERIOD_NAMES[this.period]}`;
  }

  public ngOnChanges() {
    if (this.start && this.end && this.period === PeriodEnum.custom) {
      this.dateRange = new DTDateRange(toLocalDate(this.start), toLocalDate(this.end));
    }
  }

  public identify(index: number, item?: PeriodMenuItem): string | number {
    return item?.id || index;
  }

  public selectedDate(date: Date): void {
    if (!this.dateRange || !!this.dateRange.end || (this.dateRange.start && this.dateRange.start > date)) {
      this.dateRange = new DTDateRange(date, null);
      return;
    }

    this.period = PeriodEnum.custom;
    this.dateRange = new DTDateRange(this.dateRange.start, date);
    this.emitChange();
  }

  public selectPeriod(period: PeriodMenuItem) {
    this.period = period.id;
    this.dateRange = void 0;
    this.emitChange();
  }

  public onCalendarClose() {
    if (!this.dateRange) return;

    const { start, end } = this.dateRange;
    if (!start || !end) {
      this.dateRange = void 0;
    }
  }

  public removeFilter(e: MouseEvent) {
    e.stopPropagation();
    this.clearAndApply();
    this.remove.emit();
  }

  public clearAndApply() {
    this.period = void 0;
    this.dateRange = void 0;
    this.emitChange();
  }

  private emitChange() {
    this.menuTrigger?.closeMenu();

    if (!this.period) {
      this.apply.emit({});
      return;
    }

    const dt = this.dateRange;

    const [start, end] =
      !dt || !dt.start || !dt.end //
        ? getUTCDateRangeByPeriod(this.period)
        : getUTCDateRangeByDate(dt.start, dt.end);

    this.apply.emit({ start, end, period: this.period });
  }
}
